import { CardType } from '@scale/genai-compass-shared/cardTypes';
import { CompassConfigs, CompassVariant } from '@scale/genai-compass-shared/compassVariants';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useUser } from 'react-scale/components/ui/UserContext';
import { useMemo } from 'react';
import { unique } from 'remeda';
import { genAICardUIs } from 'react-scale/corp/views/DataCompassCardsV2/genAICardUIs';

export const useEnabledCardTypes = (compassVariant: CompassVariant) => {
  const user = useUser();
  const enableTaskResponseCard = useUnleashFeatureFlag(
    'compass-enable-task-response-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableUpdateMetadataCard = useUnleashFeatureFlag(
    'compass-enable-update-metadata-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableJoinCard = useUnleashFeatureFlag(
    'compass-enable-join-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableTaskActionsOnPlayground = useUnleashFeatureFlag(
    'compass-enable-task-actions-on-playground',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableTransformScriptCard = useUnleashFeatureFlag(
    'compass-enable-transform-script-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableCdsUploadCard = useUnleashFeatureFlag(
    'compass-enable-cds-upload-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableExportToGoogleSheetsCard = useUnleashFeatureFlag(
    'compass-enable-export-to-google-sheets-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableVellumCard = useUnleashFeatureFlag(
    'compass-enable-vellum-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableSaveToSnowflakeCard = useUnleashFeatureFlag(
    'compass-card-snowflake-write',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableTranscribeAudioCard = useUnleashFeatureFlag(
    'compass-enable-audio-transcription-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableResponseBackfillCard = useUnleashFeatureFlag(
    'compass-enable-response-backfill-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;
  const enableSweapPassAtKCard = useUnleashFeatureFlag(
    'compass-enable-sweap-pass-at-k-card',
    user ? { userId: user.id } : {},
    false,
  ) as boolean;

  return useMemo(() => {
    return unique(CompassConfigs[compassVariant].enabledCardTypes)
      .filter(cardType => cardType in genAICardUIs)
      .filter(cardType => {
        switch (cardType) {
          case CardType.TASK_RESPONSE:
            return enableTaskResponseCard;
          case CardType.UPDATE_METADATA:
            return enableUpdateMetadataCard;
          case CardType.JOIN_ADDITIONAL_DATA:
            return enableJoinCard;
          case CardType.TRANSFORM_SCRIPT:
            return enableTransformScriptCard;
          case CardType.VELLUM:
            return enableVellumCard;
          case CardType.SAVE_TO_SNOWFLAKE:
            return enableSaveToSnowflakeCard;
          case CardType.CDS_UPLOAD:
            return enableCdsUploadCard;
          case CardType.EXPORT_TO_GOOGLE_SHEETS:
            return enableExportToGoogleSheetsCard;
          case CardType.TASK_ACTIONS:
            return enableTaskActionsOnPlayground || compassVariant !== CompassVariant.PLAYGROUND;
          case CardType.TRANSCRIBE_AUDIO:
            return enableTranscribeAudioCard;
          case CardType.RESPONSE_BACKFILL:
            return enableResponseBackfillCard;
          case CardType.SWEAP_PASS_AT_K:
            return enableSweapPassAtKCard;
          default:
            return true;
        }
      });
  }, [
    compassVariant,
    enableTaskResponseCard,
    enableUpdateMetadataCard,
    enableJoinCard,
    enableTaskActionsOnPlayground,
    enableTransformScriptCard,
    enableCdsUploadCard,
    enableExportToGoogleSheetsCard,
    enableVellumCard,
    enableSaveToSnowflakeCard,
    enableTranscribeAudioCard,
    enableResponseBackfillCard,
    enableSweapPassAtKCard,
  ]);
};
